'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useRouter, usePathname } from 'next/navigation';

interface AuthContextType {
    user: any | null;
    b2bEntity: any | null; // Entidad asociada en Supabase
    loading: boolean;
    logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    b2bEntity: null,
    loading: true,
    logout: async () => { }
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<any | null>(() => {
        if (typeof window !== 'undefined') {
            try {
                const s = localStorage.getItem('ftk_user');
                return s ? JSON.parse(s) : null;
            } catch (_) { return null; }
        }
        return null;
    });
    const [b2bEntity, setB2bEntity] = useState<any | null>(() => {
        if (typeof window !== 'undefined') {
            try {
                const s = localStorage.getItem('merchant_b2b_entity');
                return s ? JSON.parse(s) : null;
            } catch (_) { return null; }
        }
        return null;
    });
    const [loading, setLoading] = useState(true);
    const router = useRouter();
    const pathname = usePathname();

    useEffect(() => {
        const checkAuth = async () => {
            // Optimización: No cargar Auth Web2 si estamos en Web3 wallet
            if (pathname?.startsWith('/wallet')) {
                setLoading(false);
                return;
            }

            try {
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) throw sessionError;

                if (session) {
                    const storedUser = { uid: session.user.id, email: session.user.email };
                    setUser(storedUser);
                    localStorage.setItem('ftk_user', JSON.stringify(storedUser)); // Retro-compatibilidad

                    // Sincronizar con Supabase para obtener metadata de la Entidad/Rol
                    const { data, error } = await supabase
                        .from('b2b_entities')
                        .select('*')
                        .or(`firebase_uid.eq.${session.user.id},entity_email.eq.${session.user.email}`)
                        .maybeSingle();

                    if (!error && data) {
                        setB2bEntity(data);
                        localStorage.setItem('merchant_b2b_entity', JSON.stringify(data));
                    }
                } else {
                    // Si no hay sesión en Supabase Client, consultar sesión de merchant en backend
                    const isMerchantContext = pathname?.startsWith('/merchant') || 
                                             pathname === '/' || 
                                             (typeof window !== 'undefined' && window.location.hostname.includes('merchant.'));
                    const hasStoredMerchant = typeof localStorage !== 'undefined' && Boolean(localStorage.getItem('merchant_b2b_entity'));

                    if (isMerchantContext || hasStoredMerchant) {
                        try {
                            const res = await fetch('/api/auth/merchant-session');
                            if (res.ok) {
                                const mData = await res.json();
                                if (mData.authenticated && mData.b2bEntity) {
                                    const mUser = { uid: mData.b2bEntity.firebase_uid || mData.b2bEntity.id, email: mData.b2bEntity.entity_email };
                                    setUser(mUser);
                                    setB2bEntity(mData.b2bEntity);
                                    localStorage.setItem('ftk_user', JSON.stringify(mUser));
                                    localStorage.setItem('merchant_b2b_entity', JSON.stringify(mData.b2bEntity));
                                    setLoading(false);
                                    return;
                                }
                            } else if (res.status === 401 && !hasStoredMerchant) {
                                setUser(null);
                                setB2bEntity(null);
                                localStorage.removeItem('ftk_user');
                                localStorage.removeItem('merchant_b2b_entity');
                            }
                        } catch (err) {
                            console.error('[AuthContext] Error recuperando merchant-session:', err);
                        }
                    } else {
                        setUser(null);
                        setB2bEntity(null);
                        localStorage.removeItem('ftk_user');
                        localStorage.removeItem('merchant_b2b_entity');
                    }
                }
            } catch (e) {
                console.error("Error leyendo Auth desde Supabase:", e);
                setUser(null);
                setB2bEntity(null);
            }
            setLoading(false);
        };

        checkAuth();

        // Safety fallback: liberar el loading si supabase tarda demasiado
        const safetyLoader = setTimeout(() => {
            setLoading(prev => {
                if (prev) console.warn("Safety fallback: Forzando fin de carga de Auth.");
                return false;
            });
        }, 5000);

        // Escuchar eventos globales de login/logout entre pestañas
        window.addEventListener('auth_changed', checkAuth);
        return () => {
            clearTimeout(safetyLoader);
            window.removeEventListener('auth_changed', checkAuth);
        };
    }, []);

    const logout = async () => {
        try {
            console.log("Iniciando purga de sesión segura...");

            // 1. Purgar cookie HttpOnly vía Servidor (Impide el rertorno automático al Hub)
            await fetch('/api/auth/logout', { method: 'POST' });

            // 2. Limpiar Storage Local y Estados
            localStorage.removeItem('ftk_user');
            setUser(null);
            setB2bEntity(null);
            window.dispatchEvent(new Event('auth_changed'));

            // 3. Redirección total selectiva
            const isMaster = window.location.pathname.startsWith('/master');
            window.location.replace(isMaster ? '/master/login' : '/login');
        } catch (error) {
            console.error('Logout Fail:', error);
            localStorage.removeItem('ftk_user');
            const isMaster = window.location.pathname.startsWith('/master');
            window.location.href = isMaster ? '/master/login' : '/login';
        }
    };

    return (
        <AuthContext.Provider value={{ user, b2bEntity, loading, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
